import { useState, useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface VendorProfileModalProps {
  userId: string
  userType: string
  onClose: () => void
  onSuccess: () => void
}

export default function VendorProfileModal({ userId, userType, onClose, onSuccess }: VendorProfileModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  
  const [vendorName, setVendorName] = useState('')
  const [vendorDescription, setVendorDescription] = useState('')
  const [vendorEmail, setVendorEmail] = useState('')
  const [vendorPhone, setVendorPhone] = useState('')
  const [contactName, setContactName] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [directoryOptedIn, setDirectoryOptedIn] = useState(false)
  const [directoryAgreementConfirmed, setDirectoryAgreementConfirmed] = useState(false)
  const [vendorLogo, setVendorLogo] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Normalize userType for comparison
  const normalizedUserType = userType?.toLowerCase()
  const isVendorUser = normalizedUserType === 'broadcaster' || normalizedUserType === 'ad-creator'

  const nameInputRef = useRef<HTMLInputElement>(null)

  // Focus first input on mount and handle ESC key
  useEffect(() => {
    nameInputRef.current?.focus()
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose()
      }
    }
    
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, isSaving])

  // Fetch existing vendor data
  const { data: vendor, isLoading } = useQuery({
    queryKey: ['vendor', userId],
    queryFn: async () => {
      try {
        console.log('Fetching vendor for userId:', userId) // Debug log
        const { data, error } = await supabase
          .from('vendors')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle() // Use maybeSingle instead of single to return null instead of error when no rows

        if (error) {
          // Check for "no rows" errors - these are expected when vendor doesn't exist yet
          const isNoRowsError = 
            error.code === 'PGRST116' || 
            error.message?.includes('No rows') ||
            error.message?.includes('not found')
          
          if (!isNoRowsError) {
            console.warn('Error fetching vendor:', error)
          } else {
            console.log('No vendor found for user (this is expected if vendor not created yet)')
          }
          return null
        }
        
        console.log('Vendor data fetched:', data) // Debug log
        return data
      } catch (err: any) {
        console.warn('Exception fetching vendor:', err)
        return null
      }
    },
    enabled: !!userId && isVendorUser,
    retry: false,
  })

  useEffect(() => {
    if (vendor) {
      console.log('Vendor data loaded:', vendor) // Debug log
      setVendorName(vendor.vendor_name || vendor.name || '')
      setVendorDescription(vendor.vendor_description || vendor.description || '')
      setVendorEmail(vendor.vendor_email || vendor.email || '')
      setVendorPhone(vendor.vendor_phone || vendor.phone || '')
      setContactName(vendor.contact_name || '')
      setWebsiteUrl(vendor.website_url || '')
      setDirectoryOptedIn(vendor.directory_opted_in ?? false)
      setDirectoryAgreementConfirmed(false)
      if (vendor.vendor_logo_url || vendor.logo_url) {
        setLogoPreview(vendor.vendor_logo_url || vendor.logo_url)
      } else {
        setLogoPreview(null)
      }
    } else {
      setVendorName('')
      setVendorDescription('')
      setVendorEmail('')
      setVendorPhone('')
      setContactName('')
      setWebsiteUrl('')
      setDirectoryOptedIn(false)
      setDirectoryAgreementConfirmed(false)
      setLogoPreview(null)
    }
  }, [vendor])

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setVendorLogo(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const uploadLogo = async (file: File): Promise<string | null> => {
    if (!user) return null
    
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${userId}-${Date.now()}.${fileExt}`
      const filePath = `vendor-logos/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('vendor-logos')
        .upload(filePath, file, { upsert: true })

      if (uploadError) {
        // If bucket doesn't exist, try to create it or use a fallback
        if (uploadError.message?.includes('Bucket not found')) {
          console.warn('Vendor logos bucket not found. Logo upload skipped.')
          return null
        }
        console.error('Error uploading logo:', uploadError)
        return null
      }

      const { data } = supabase.storage
        .from('vendor-logos')
        .getPublicUrl(filePath)

      return data.publicUrl
    } catch (err) {
      console.error('Error uploading logo:', err)
      return null
    }
  }

  const saveVendor = useMutation({
    mutationFn: async (data: {
      name: string
      description: string | null
      email: string | null
      phone: string | null
      logo_url: string | null
      contact_name: string | null
      website_url: string | null
      directory_opted_in: boolean
      directory_opted_in_at: string | null
    }) => {
      if (!user) throw new Error('Not authenticated')

      let logoUrl = logoPreview

      if (vendorLogo) {
        const uploadedUrl = await uploadLogo(vendorLogo)
        if (uploadedUrl) {
          logoUrl = uploadedUrl
        }
      }

      const payload = {
        vendor_name: data.name,
        vendor_description: data.description,
        vendor_email: data.email,
        vendor_phone: data.phone,
        vendor_logo_url: logoUrl,
        contact_name: data.contact_name || null,
        website_url: data.website_url || null,
        directory_opted_in: data.directory_opted_in,
        directory_opted_in_at: data.directory_opted_in_at,
      }

      if (vendor) {
        const { error } = await supabase
          .from('vendors')
          .update(payload)
          .eq('user_id', user.id)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('vendors')
          .insert({
            user_id: user.id,
            vendor_type: userType,
            ...payload,
          })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', userId] })
      queryClient.invalidateQueries({ queryKey: ['broadcasters'] })
      queryClient.invalidateQueries({ queryKey: ['ad-creators'] })
      queryClient.invalidateQueries({ queryKey: ['directory-broadcasters'] })
      queryClient.invalidateQueries({ queryKey: ['directory-ad-creators'] })
      setIsSaving(false)
      setError(null)
      onSuccess()
      onClose()
    },
  })

  const handleSave = async () => {
    if (!user) return

    setError(null)

    if (!vendorName.trim()) {
      setError('Vendor name is required')
      return
    }

    if (directoryOptedIn) {
      if (!vendorEmail.trim()) {
        setError('Contact email is required to appear in the directory.')
        return
      }
      if (!directoryAgreementConfirmed) {
        setError('Please confirm you agree to be contacted via the contact information provided.')
        return
      }
    }

    const nowIso = new Date().toISOString()
    // When opting in for the first time set directory_opted_in_at to now; when already opted in preserve it; when opting out clear it
    const directoryOptedInAt = directoryOptedIn
      ? (vendor?.directory_opted_in ? (vendor.directory_opted_in_at ?? nowIso) : nowIso)
      : null

    setIsSaving(true)
    try {
      await saveVendor.mutateAsync({
        name: vendorName.trim(),
        description: vendorDescription.trim() || null,
        email: vendorEmail.trim() || null,
        phone: vendorPhone.trim() || null,
        logo_url: null,
        contact_name: contactName.trim() || null,
        website_url: websiteUrl.trim() || null,
        directory_opted_in: directoryOptedIn,
        directory_opted_in_at: directoryOptedInAt,
      })
    } catch (err: any) {
      setError(err.message || 'Failed to save vendor profile')
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vendor-profile-title"
      >
        <div
          className="rounded-lg p-6 max-w-2xl w-full"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '2px solid var(--bg-tertiary)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <p style={{ color: 'var(--text-primary)' }}>Loading vendor profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="vendor-profile-title"
    >
      <div
        className="rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 
            id="vendor-profile-title"
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            {vendor ? 'Edit Vendor Profile' : 'Create Vendor Profile'}
          </h2>
          <button
            onClick={onClose}
            className="p-2"
            aria-label="Close dialog"
            style={{ color: 'var(--text-primary)' }}
            disabled={isSaving}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
        {/* Vendor Type (read-only) */}
        <div>
          <label 
            htmlFor="vendor-type" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Vendor Type
          </label>
          <input
            type="text"
            id="vendor-type"
            value={userType === 'broadcaster' ? 'Broadcaster' : userType === 'ad-creator' ? 'Ad-Creator' : userType}
            disabled
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-secondary)',
              cursor: 'not-allowed',
            }}
          />
        </div>

        {/* Vendor Name */}
        <div>
          <label 
            htmlFor="vendor-name" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Vendor Name
          </label>
          <input
            type="text"
            ref={nameInputRef}
            id="vendor-name"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
            aria-required="true"
          />
        </div>

        {/* Vendor Description */}
        <div>
          <label 
            htmlFor="vendor-description" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Vendor Description
          </label>
          <textarea
            id="vendor-description"
            value={vendorDescription}
            onChange={(e) => setVendorDescription(e.target.value)}
            rows={4}
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Vendor Email */}
        <div>
          <label 
            htmlFor="vendor-email" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Vendor Email
          </label>
          <input
            type="email"
            id="vendor-email"
            value={vendorEmail}
            onChange={(e) => setVendorEmail(e.target.value)}
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Vendor Phone */}
        <div>
          <label 
            htmlFor="vendor-phone" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Vendor Phone
            <span className="text-sm font-normal ml-1" style={{ color: 'var(--text-secondary)' }}>(optional)</span>
          </label>
          <input
            type="tel"
            id="vendor-phone"
            value={vendorPhone}
            onChange={(e) => setVendorPhone(e.target.value)}
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Contact name (for directory) */}
        <div>
          <label 
            htmlFor="contact-name" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Contact name
            <span className="text-sm font-normal ml-1" style={{ color: 'var(--text-secondary)' }}>(optional)</span>
          </label>
          <input
            type="text"
            id="contact-name"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Name of the person to contact"
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Website URL */}
        <div>
          <label 
            htmlFor="website-url" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Website URL
            <span className="text-sm font-normal ml-1" style={{ color: 'var(--text-secondary)' }}>(optional)</span>
          </label>
          <input
            type="url"
            id="website-url"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://..."
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Directory opt-in */}
        <div
          className="rounded p-4 border-2"
          style={{
            borderColor: 'var(--bg-tertiary)',
            backgroundColor: 'var(--bg-primary)',
          }}
        >
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="directory-opted-in"
              checked={directoryOptedIn}
              onChange={(e) => {
                setDirectoryOptedIn(e.target.checked)
                if (!e.target.checked) setDirectoryAgreementConfirmed(false)
              }}
              className="mt-1"
              aria-describedby="directory-description directory-agreement"
            />
            <div>
              <label htmlFor="directory-opted-in" className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                Show in {normalizedUserType === 'broadcaster' ? 'Broadcasters' : 'AD Creators'} directory
              </label>
              <p id="directory-description" className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                Your listing will appear on the Resources page so others can find your contact details. Contact email is required.
              </p>
            </div>
          </div>
          {directoryOptedIn && (
            <div className="mt-4 pl-7">
              <p id="directory-agreement" className="text-sm mb-2" style={{ color: 'var(--text-primary)' }}>
                Registering as a {normalizedUserType === 'broadcaster' ? 'Broadcaster' : 'AD Creator'} in the directory means you agree to be contacted via the contact information you have provided.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="directory-agreement-confirm"
                  checked={directoryAgreementConfirmed}
                  onChange={(e) => setDirectoryAgreementConfirmed(e.target.checked)}
                  aria-required={directoryOptedIn}
                  className="rounded"
                />
                <label htmlFor="directory-agreement-confirm" className="text-sm" style={{ color: 'var(--text-primary)' }}>
                  I agree
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Vendor Logo */}
        <div>
          <label 
            htmlFor="vendor-logo" 
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Vendor Logo
          </label>
          {logoPreview && (
            <div className="mb-4">
              <img
                src={logoPreview}
                alt="Vendor logo preview"
                className="max-w-32 max-h-32 object-contain"
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  padding: '8px',
                  borderRadius: '4px',
                }}
              />
            </div>
          )}
          <input
            type="file"
            id="vendor-logo"
            accept="image/*"
            onChange={handleLogoChange}
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

          {error && (
            <div 
              role="alert"
              className="p-3 rounded"
              style={{
                backgroundColor: 'var(--color-error)',
                color: 'var(--text-inverse)',
              }}
            >
              {error}
            </div>
          )}

          {/* Save Button */}
          <div className="flex gap-4 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-6 py-2 font-semibold rounded"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: isSaving
                  ? 'var(--bg-tertiary)'
                  : 'var(--bg-interactive)',
                color: 'var(--text-inverse)',
              }}
            >
              {isSaving ? 'Saving...' : vendor ? 'Update Profile' : 'Create Profile'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

